// Keep the on-device JSON cache small enough for Safari (~5MB localStorage).
// Firestore's multi-tab mutation log also uses localStorage; a full app dump
// plus pending writes is what triggered QuotaExceededError.

const DATE_KEY = /^\d{4}-\d{2}-\d{2}$/;

export const isQuotaExceeded = (err) => {
  if (!err) return false;
  if (err.name === 'QuotaExceededError' || err.code === 22) return true;
  return /quota/i.test(String(err.message || ''));
};

export const isConstrainedWebStorage = (ua = typeof navigator !== 'undefined' ? navigator.userAgent : '', nav = typeof navigator !== 'undefined' ? navigator : null) => {
  const agent = String(ua || '');
  if (/iP(hone|ad|od)/i.test(agent)) return true;
  if (nav && nav.platform === 'MacIntel' && Number(nav.maxTouchPoints) > 1) return true;
  return /Safari/i.test(agent) && !/Chrome|Chromium|Edg|Android/i.test(agent);
};

export const keepRecentDateMap = (input, keepDays, today = new Date()) => {
  if (!input || typeof input !== 'object' || Array.isArray(input)) return {};
  const cutoff = new Date(today);
  cutoff.setHours(0, 0, 0, 0);
  cutoff.setDate(cutoff.getDate() - Math.max(1, keepDays));
  const cutoffKey = cutoff.toISOString().slice(0, 10);
  const out = {};
  Object.entries(input).forEach(([key, value]) => {
    if (!DATE_KEY.test(key) || key >= cutoffKey) out[key] = value;
  });
  return out;
};

const slimMemoryEntry = (entry) => {
  if (!entry || typeof entry !== 'object') return entry;
  const {
    aliases: _aliases,
    ...rest
  } = entry;
  return rest;
};

export const slimCalorieMemory = (memory, maxEntries = 300) => {
  if (!memory || typeof memory !== 'object' || Array.isArray(memory)) return {};
  const entries = Object.entries(memory).map(([key, value]) => [key, slimMemoryEntry(value)]);
  if (entries.length <= maxEntries) {
    return Object.fromEntries(entries);
  }
  entries.sort((a, b) => String(b[1]?.updatedAt || '').localeCompare(String(a[1]?.updatedAt || '')));
  return Object.fromEntries(entries.slice(0, maxEntries));
};

export const slimForLocalCache = (data, { aggressive = false } = {}) => {
  if (!data || typeof data !== 'object') return data;
  const logDays = aggressive ? 14 : 60;
  const planDays = aggressive ? 10 : 21;
  const memoryCap = aggressive ? 80 : 300;
  return {
    ...data,
    mealLog: keepRecentDateMap(data.mealLog, logDays),
    moneyLog: keepRecentDateMap(data.moneyLog, logDays),
    stepLog: keepRecentDateMap(data.stepLog, logDays),
    dayPlans: keepRecentDateMap(data.dayPlans, planDays),
    calorieMemory: slimCalorieMemory(data.calorieMemory, memoryCap)
  };
};

export const collectLocalStorageKeys = (storage) => {
  const keys = [];
  if (!storage) return keys;
  try {
    for (let i = 0; i < storage.length; i += 1) {
      const key = storage.key(i);
      if (key) keys.push(key);
    }
  } catch {
    /* ignore */
  }
  return keys;
};

export const reclaimWebStorage = (storage, { dropFirestoreWebStorage = false } = {}) => {
  if (!storage) return 0;
  let removed = 0;
  collectLocalStorageKeys(storage).forEach((key) => {
    const drop =
      key.startsWith('rpg-tracker:cache:v0:')
      || (dropFirestoreWebStorage && key.startsWith('firestore_'));
    if (!drop) return;
    try {
      storage.removeItem(key);
      removed += 1;
    } catch {
      /* ignore */
    }
  });
  return removed;
};

export const writeJsonToStorage = (storage, key, value) => {
  storage.setItem(key, JSON.stringify(value));
};

// Last-ditch payload: the fields nothing else can rebuild. Integrated book
// ideas and goals sit with meals and quests so a full localStorage cannot drop them.
export const coreForLocalCache = (data) => {
  if (!data || typeof data !== 'object') return data;
  return {
    calorieMemory: slimCalorieMemory(data.calorieMemory, 150),
    mealLog: keepRecentDateMap(data.mealLog, 7),
    healthProfile: data.healthProfile,
    todos: data.todos,
    todoMap: data.todoMap,
    habits: data.habits,
    integrations: data.integrations,
    goals: data.goals
  };
};

export const writeLocalCacheRecord = (storage, key, data, savedAt, { reclaimFirestore = false } = {}) => {
  if (!storage || !key || !data) return false;
  const attempts = [
    slimForLocalCache(data),
    slimForLocalCache(data, { aggressive: true }),
    coreForLocalCache(data)
  ];
  for (let i = 0; i < attempts.length; i += 1) {
    try {
      writeJsonToStorage(storage, key, { savedAt, data: attempts[i] });
      return true;
    } catch (err) {
      if (!isQuotaExceeded(err)) throw err;
      reclaimWebStorage(storage, { dropFirestoreWebStorage: reclaimFirestore || i > 0 });
    }
  }
  // Keep whatever is already stored. Wiping the key left iOS Safari with nothing to
  // merge from on the next launch, which is exactly the state that surfaced the
  // 27-item GLM seed as if it were the user's saved meals.
  return false;
};
